/**
 * Executes an asynchronous function with a retry mechanism.
 * If the function fails, it waits for a specified delay and retries,
 * using an exponential backoff strategy.
 * @param {Function} fn The asynchronous function to retry.
 * @param {Array} args Arguments to pass to the function.
 * @param {object} options Retry options.
 * @param {number} [options.retries=5] Maximum number of retry attempts.
 * @param {number} [options.delay=1000] Initial delay in milliseconds before retrying.
 * @param {number} [options.backoff=2] Factor by which the delay increases with each retry.
 * @param {Function} [options.shouldRetry=(e) => true] A predicate to decide if a retry should be performed.
 * @returns {Promise<*>} The return value of the asynchronous function.
 */
const retry = async (fn, args, options) => {
	let { retries, delay, backoff, shouldRetry } = {
		retries: 5,
		delay: 1000,
		backoff: 2,
		shouldRetry: () => true,
		...options,
	};

	for (let i = 0; i < retries; i++) {
		try {
			return await fn(...args);
		} catch (e) {
			if (!shouldRetry(e) || i === retries - 1) {
				throw e;
			}
			console.log(`Attempt ${i + 1} failed. Retrying in ${delay}ms...`);
			await new Promise(resolve => setTimeout(resolve, delay));
			delay *= backoff;
		}
	}
};

module.exports = retry;
